import { useToastContext } from "@ttc3k/trekker";
import { Links } from "gql/generated";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SocialLinks from "social-links";

const linkIcons = {
  website: "globe.svg",
  instagram: "social-instagram.svg",
  x: "social-x.svg",
  bookNow: "social-",
  youtube: "social-youtube.svg",
  pinterest: "social-pinterest.svg",
  facebook: "social-facebook.svg",
  googleMyBusiness: "social-google.svg",
  tiktok: "social-tiktok.svg",
  tripAdvisor: "social-tripadvisor.svg",
};

export type SocialLink = {
  Icon: string;
  url: string;
  alt: string;
};

export const useSocialLinks = (
  links: Links | null | undefined,
  errorOverride?: { title: string; description: string },
) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  /**
   * Extract the socials links from the links array
   */
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  useEffect(() => {
    const allLinks = links ?? {};
    const keys = Object.keys(links ?? {}).filter(
      (key) => allLinks[key as keyof typeof allLinks] && key !== "__typename",
    );
    keys.sort((a, b) => (a.valueOf() > b.valueOf() ? 1 : -1));

    const socialLinks: SocialLink[] = keys.map((key) => ({
      Icon: linkIcons[key as keyof typeof linkIcons],
      url: allLinks[key as keyof typeof allLinks] as string,
      alt: key,
    }));
    setSocialLinks(socialLinks);
  }, [links]);

  /**
   * Open the link in a new window
   * @param link
   */
  const handleSocialClick = (link: SocialLink) => {
    let url = "";
    switch (link.alt) {
      case "facebook":
      case "instagram":
      case "pinterest":
      case "tiktok":
      case "youtube": {
        const socialLinks = new SocialLinks();
        url = socialLinks.sanitize(link.alt, link.url);
        break;
      }
      default:
        url = !/^(https?:\/\/)/.test(link.url)
          ? `https://${link.url}`
          : link.url;
    }
    try {
      window.open(url);
    } catch {
      toastFactory.create({
        title: errorOverride?.title ?? t("core:ERROR.TITLE"),
        description:
          errorOverride?.description ?? t("core:OPERATOR_MODAL.ERRORS.LINK"),
      });
    }
  };

  return {
    socialLinks,
    handleSocialClick,
  };
};
